import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Breadcrumb Component (Preline UI)
 *
 * A reusable breadcrumb navigation component using Preline UI styling.
 * Shows navigation hierarchy with icons and separators.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of breadcrumb items [{label: string, icon?: string, href?: string, onClick?: function}]
 * @param {string} [props.separator='chevron'] - Separator type ('chevron' or 'slash')
 *
 * @example
 * <Breadcrumb
 *   items={[
 *     { label: 'Users', icon: 'lucide:users', onClick: () => navigate('/users') },
 *     { label: 'John Doe' } // Current page (no onClick)
 *   ]}
 *   separator="chevron"
 * />
 */
export function Breadcrumb({ items, separator = 'chevron' }) {
  const separatorIcon = separator === 'slash' ? '/' : 'lucide:chevron-right';
  const isSeparatorText = separator === 'slash';

  return (
    <ol className="flex items-center whitespace-nowrap mb-6">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isClickable = !isLast && (item.onClick || item.href);

        return (
          <li
            key={index}
            className="inline-flex items-center"
          >
            {/* Item Content */}
            {isClickable ? (
              <button
                onClick={item.onClick}
                className="flex items-center text-sm text-muted hover:text-brand focus:outline-none focus:text-brand cursor-pointer transition-colors"
                type="button"
              >
                {item.icon && (
                  <Icon icon={item.icon} className="shrink-0 size-4 mr-2" />
                )}
                {item.label}
              </button>
            ) : (
              <span className="flex items-center text-sm font-semibold text-default truncate">
                {item.icon && (
                  <Icon icon={item.icon} className="shrink-0 size-4 mr-2" />
                )}
                {item.label}
              </span>
            )}

            {/* Separator */}
            {!isLast && (
              <>
                {isSeparatorText ? (
                  <span className="mx-3 text-subtle">/</span>
                ) : (
                  <Icon
                    icon={separatorIcon}
                    className="shrink-0 mx-3 size-4 text-subtle"
                  />
                )}
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      href: PropTypes.string,
      onClick: PropTypes.func,
    })
  ).isRequired,
  separator: PropTypes.oneOf(['chevron', 'slash']),
};
